import { CreditCard, ExternalLink } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { Badge, Button, Card, Spinner } from '@/components/ui'
import { PageShell } from '@/components/common/PageShell'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { useBilling, useOpenBillingPortal, useStartCheckout } from '../hooks/useBilling'
import { describeStatus } from './statusCopy'
import { PlanCard } from './PlanCard'

const TONE_TO_BADGE = {
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  neutral: 'neutral',
} as const

/**
 * Plan, trial and payment state for this workspace.
 *
 * Every number on this page comes from the server, including which plan is
 * current and whether the workspace can still be written to. Nothing is derived
 * from a URL or from what the browser last saw — the same rule the API follows.
 */
export function BillingPage(): React.ReactElement {
  const [params] = useSearchParams()
  const cancelled = params.get('checkout') === 'cancelled'
  const { data, isPending, isError, error, refetch } = useBilling()
  const checkout = useStartCheckout()
  const portal = useOpenBillingPortal()

  return (
    <PageShell
      icon={CreditCard}
      breadcrumbItems={[{ label: 'Billing' }]}
      title="Plan & billing"
      description="What this workspace is on, what it costs, and when the next payment is due."
      actions={
        data?.hasSubscription ? (
          <Button variant="secondary" loading={portal.isPending} onClick={() => portal.mutate()}>
            Manage in Stripe
            <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        ) : undefined
      }
    >
      {isPending ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : isError || !data ? (
        <QueryErrorState error={error} onRetry={() => refetch()} />
      ) : !data.configured ? (
        // An environment with no Stripe keys. Saying so beats every button
        // failing one at a time.
        <Card className="p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Billing is not configured on this environment, so plans cannot be purchased here. Your
            current plan is <strong>{data.planName ?? 'not set'}</strong>.
          </p>
        </Card>
      ) : (
        <div className="space-y-5">
          {cancelled && (
            // Returning from an abandoned Checkout is not a failure, and the
            // page must not imply the card was declined.
            <Card className="border-gray-200 p-4 text-sm text-gray-600 dark:text-gray-400">
              Checkout was cancelled. Nothing has been charged and your plan is unchanged.
            </Card>
          )}

          <StatusCard data={data} />

          <div>
            <h2 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
              {data.hasSubscription ? 'Plans' : 'Choose a plan'}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.plans.map((plan) => (
                <PlanCard
                  key={plan.key}
                  plan={plan}
                  trialDaysLeft={data.checkoutTrial ? data.trial.daysLeft : null}
                  seats={data.seats.count}
                  current={plan.key === data.plan}
                  busy={checkout.isPending}
                  // With a live or unpaid subscription, switching plan or paying
                  // belongs in Stripe's portal — a second checkout would start a
                  // parallel subscription. A cancelled one can be replaced.
                  disabled={!data.canResubscribe || data.billingExempt}
                  customDomainsAvailable={data.customDomainsAvailable}
                  onChoose={(key) => checkout.mutate(key)}
                />
              ))}
            </div>
            {data.hasSubscription && (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                {data.status === 'unpaid' ? (
                  <>
                    Pay the open invoice in <strong>Manage in Stripe</strong> above to make the
                    workspace writable again.
                  </>
                ) : (
                  <>
                    To change plan or update your card, use <strong>Manage in Stripe</strong> above.
                    Changes appear here within a few seconds.
                  </>
                )}
              </p>
            )}
            {!data.billingExempt && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Billed per active user: {data.seats.count}{' '}
                {data.seats.count === 1 ? 'seat' : 'seats'} today. Adding or removing users adjusts
                the next invoice pro rata.
              </p>
            )}
          </div>
        </div>
      )}
    </PageShell>
  )
}

function StatusCard({ data }: { data: ReturnType<typeof useBilling>['data'] & object }) {
  const status = describeStatus(data)
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {data.planName ?? 'No plan'}
            </span>
            <Badge variant={TONE_TO_BADGE[status.tone]} dot>
              {status.label}
            </Badge>
          </div>
          <p className="mt-1.5 max-w-prose text-sm text-gray-600 dark:text-gray-400">
            {status.detail}
          </p>
          {/* Stripe bills for one plan, an operator can grant another. Showing
              both beats silently picking a winner. */}
          {data.billedPlan && data.plan && data.billedPlan !== data.plan && (
            <p className="mt-2 text-xs text-warning">
              Your subscription pays for the {data.billedPlan} plan while this workspace is set to{' '}
              {data.plan}. Contact support so the two can be lined up.
            </p>
          )}
        </div>
        {!data.canWrite && (
          <span className="rounded-md bg-danger/10 px-2.5 py-1 text-xs font-medium text-danger">
            Read-only
          </span>
        )}
      </div>
    </Card>
  )
}
